/* ============================================================
   Restroom Resources — Location Directory
   Leaflet map + searchable list
   ============================================================ */
(function () {
  'use strict';

  // Expand compound labels for search without changing their displayed wording.
  function searchableCounty(label) {
    const normalized = label.toLowerCase();
    if (!/\bcounties$/.test(normalized)) return normalized;
    const names = normalized.replace(/\s+counties$/, '').split(/\s*,\s*|\s+and\s+/);
    return normalized + ' ' + names.filter(Boolean).map(function (name) {
      return name + ' county';
    }).join(' ');
  }

  // ── Wait for Leaflet ──────────────────────────────────────
  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  var leafletPromise = null;
  function loadLeaflet() {
    if (typeof L !== 'undefined') return Promise.resolve();
    if (leafletPromise) return leafletPromise;
    leafletPromise = new Promise(function (resolve, reject) {
      if (!document.querySelector('link[data-leaflet]')) {
        var stylesheet = document.createElement('link');
        stylesheet.rel = 'stylesheet';
        stylesheet.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        stylesheet.crossOrigin = '';
        stylesheet.dataset.leaflet = 'true';
        document.head.appendChild(stylesheet);
      }
      var script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.crossOrigin = '';
      script.onload = function () { resolve(); };
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return leafletPromise;
  }

  onReady(function () {
    const mapEl = document.getElementById('locations-map');
    if (!mapEl) return;
    var started = false;
    var start = function () {
      if (started) return;
      started = true;
      loadLeaflet().then(function () { initMap(mapEl); }).catch(function () {
        mapEl.setAttribute('aria-label', 'Service area map is temporarily unavailable');
      });
    };
    var target = mapEl.closest('section') || mapEl.parentElement || mapEl;
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) {
          observer.disconnect();
          start();
        }
      }, { rootMargin: '600px 0px' });
      observer.observe(target);
    } else {
      start();
    }
  });

  function initMap(mapEl) {
    if (typeof L === 'undefined') return;

    // ── Map init ─────────────────────────────────────────────
    const splitEl = mapEl.parentElement; // .locations-split — store before any DOM moves

    const map = L.map('locations-map', {
      center: [40.1, -74.55],
      zoom: 8,
      zoomControl: true,
      scrollWheelZoom: false,
    });
    window._locMap = map;

    L.tileLayer('https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=__CARTO_BASEMAP_KEY__', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);

    // ── Custom marker ─────────────────────────────────────────
    function makeIcon(active) {
      return L.divIcon({
        className: '',
        html: `<div class="loc-marker${active ? ' loc-marker--active' : ''}"></div>`,
        // Keep the visible 14px marker, but give keyboard/touch users a 24px target.
        iconSize: [24, 24],
        iconAnchor: [12, 12],
        popupAnchor: [0, -10],
      });
    }

    // ── Collect items from DOM ────────────────────────────────
    const items = Array.from(document.querySelectorAll('.loc-item'));
    const markers = [];
    let activeMarker = null;
    let activeItem = null;

    items.forEach(function (li) {
      const lat = parseFloat(li.dataset.lat);
      const lng = parseFloat(li.dataset.lng);
      const city = li.dataset.city;
      const county = li.dataset.county;
      const state = li.dataset.state;
      const stateAbbr = li.dataset.stateAbbr;
      const url = li.dataset.url || 'https://book.restroomresources.com';
      const phoneEl = li.querySelector('.loc-phone');
      const phone = (phoneEl && phoneEl.textContent.trim()) || '732.592.1717';
      if (isNaN(lat) || isNaN(lng)) return;

      const marker = L.marker([lat, lng], { icon: makeIcon(false), title: `${city}, ${state}` })
        .on('add', function () {
          this.getElement().setAttribute('aria-label', `${city}, ${state}`);
        })
        .addTo(map)
        .bindPopup(
          `<div class="loc-popup">
            <strong>${city}, ${state}</strong>
            <span>${county}</span>
            <span class="loc-popup-serve">Serving ${city} & surrounding areas</span>
            <span class="loc-popup-phone">${phone}</span>
            <span class="loc-popup-hours">Mon–Fri 7:30 AM–5 PM<br>Sat 8 AM–2 PM · Sun Closed</span>
            <a href="${url}" class="loc-popup-btn">View Location ↗</a>
          </div>`,
          { maxWidth: 220, closeButton: false }
        );

      markers.push({ marker, li, city, county, state, stateAbbr, url });

      marker.on('click', function () {
        activateLocation({ marker, li, city, county }, true);
      });

      li.addEventListener('click', function (e) {
        // let the anchor handle navigation naturally; we just highlight
        activateLocation({ marker, li, city, county }, false);
        map.flyTo([lat, lng], 12, { duration: 0.6 });
        marker.openPopup();
      });
    });

    // Focus each directory on the markets it actually contains. State hubs
    // therefore frame only their own state inventory, while the aggregate
    // homepage directory frames the complete four-state service area.
    if (markers.length) {
      const directoryState = document.getElementById('service-areas').dataset.directoryState;
      const bounds = L.latLngBounds(markers.map(function (entry) {
        return [
          parseFloat(entry.li.dataset.lat),
          parseFloat(entry.li.dataset.lng),
        ];
      }));
      map.fitBounds(bounds, {
        padding: [32, 32],
        maxZoom: directoryState === 'all' ? 7 : 9,
      });
    }

    function activateLocation(entry, fromMap) {
      // Reset previous
      if (activeMarker) activeMarker.setIcon(makeIcon(false));
      if (activeItem) activeItem.classList.remove('loc-item--active');

      entry.marker.setIcon(makeIcon(true));
      entry.li.classList.add('loc-item--active');

      if (!fromMap) {
        // scroll list item into view
        entry.li.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        // scroll list to match
        entry.li.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      activeMarker = entry.marker;
      activeItem = entry.li;
    }

    // ── Search ───────────────────────────────────────────────
    const searchInput = document.getElementById('loc-search');
    const suggestionsEl = document.getElementById('loc-suggestions');
    const countEl = document.getElementById('loc-count');
    const noResultsEl = document.getElementById('loc-no-results');
    const groups = Array.from(document.querySelectorAll('.loc-group'));
    let activeSuggestionIndex = -1;

    if (!searchInput) return;

    searchInput.addEventListener('input', function () {
      const q = this.value.trim().toLowerCase();
      filterLocations(q);
      updateSuggestions(q);
    });

    searchInput.addEventListener('blur', function () {
      hideSuggestions();
    });

    searchInput.addEventListener('keydown', function (e) {
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && suggestionsEl.hidden) {
        updateSuggestions(this.value.trim().toLowerCase(), true);
      }
      const options = Array.from(suggestionsEl.querySelectorAll('[role="option"]'));
      if (e.key === 'Escape') {
        this.value = '';
        filterLocations('');
        hideSuggestions();
      } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && options.length) {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        activeSuggestionIndex = activeSuggestionIndex < 0
          ? (delta > 0 ? 0 : options.length - 1)
          : (activeSuggestionIndex + delta + options.length) % options.length;
        setActiveSuggestion(options);
      } else if (e.key === 'Home' && options.length) {
        e.preventDefault();
        activeSuggestionIndex = 0;
        setActiveSuggestion(options);
      } else if (e.key === 'End' && options.length) {
        e.preventDefault();
        activeSuggestionIndex = options.length - 1;
        setActiveSuggestion(options);
      } else if (e.key === 'Enter' && activeSuggestionIndex >= 0 && options[activeSuggestionIndex]) {
        e.preventDefault();
        selectSuggestion(options[activeSuggestionIndex]);
      }
    });

    function filterLocations(q, selectedUrl) {
      let visible = 0;

      groups.forEach(function (group) {
        const groupItems = group.querySelectorAll('.loc-item');
        let groupVisible = 0;

        groupItems.forEach(function (li) {
          const city = li.dataset.city.toLowerCase();
          const county = searchableCounty(li.dataset.county);
          const state = li.dataset.state.toLowerCase();
          const stateAbbr = li.dataset.stateAbbr.toLowerCase();
          const match = selectedUrl
            ? li.dataset.url === selectedUrl
            : !q || city.includes(q) || county.includes(q) || state.includes(q) || stateAbbr === q;
          li.hidden = !match;
          if (match) groupVisible++;
        });

        group.hidden = groupVisible === 0;
        visible += groupVisible;
      });

      const directoryState = document.getElementById('service-areas').dataset.directoryState;
      const scope = directoryState === 'all'
        ? 'across four states'
        : `across ${items[0] ? items[0].dataset.state : 'this state'}`;
      countEl.innerHTML = `<strong>${visible}</strong> location${visible !== 1 ? 's' : ''} ${scope}`;
      noResultsEl.hidden = visible > 0;
    }

    function updateSuggestions(q, allowEmpty) {
      searchInput.removeAttribute('aria-activedescendant');
      if (!q && !allowEmpty) { hideSuggestions(); return; }

      const matches = markers.filter(function (m) {
        return `${m.city}, ${m.state}`.toLowerCase().includes(q) || searchableCounty(m.county).includes(q) ||
          m.state.toLowerCase().includes(q) || m.stateAbbr.toLowerCase() === q;
      }).slice(0, 6);

      if (!matches.length) { hideSuggestions(); return; }

      suggestionsEl.innerHTML = matches.map(function (m, index) {
        return `<li role="option" id="loc-suggestion-${index}" aria-selected="false" data-city="${m.city}" data-state="${m.state}" data-url="${m.url}">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>
          <span class="sug-city">${m.city}, ${m.state}</span>
          <span class="sug-county">${m.county}</span>
        </li>`;
      }).join('');

      suggestionsEl.removeAttribute('hidden');
      searchInput.setAttribute('aria-expanded', 'true');
      activeSuggestionIndex = -1;

      suggestionsEl.querySelectorAll('li').forEach(function (sugLi) {
        sugLi.addEventListener('mousedown', function (event) {
          event.preventDefault();
          selectSuggestion(this);
        });
      });
    }

    function setActiveSuggestion(options) {
      options.forEach(function (option, index) {
        const selected = index === activeSuggestionIndex;
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
      });
      const active = options[activeSuggestionIndex];
      if (active) {
        searchInput.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      }
    }

    function selectSuggestion(suggestion) {
      const city = suggestion.dataset.city;
      const state = suggestion.dataset.state;
      const url = suggestion.dataset.url;
      searchInput.value = `${city}, ${state}`;
      filterLocations('', url);
      hideSuggestions();

      const match = markers.find(function (m) { return m.url === url; });
      if (match) {
        activateLocation(match, false);
        map.flyTo([parseFloat(match.li.dataset.lat), parseFloat(match.li.dataset.lng)], 13, { duration: 0.7 });
        match.marker.openPopup();
      }
    }

    function hideSuggestions() {
      suggestionsEl.setAttribute('hidden', '');
      suggestionsEl.innerHTML = '';
      searchInput.setAttribute('aria-expanded', 'false');
      searchInput.removeAttribute('aria-activedescendant');
      activeSuggestionIndex = -1;
    }

    // ── Mobile "Show Map" toggle ──────────────────────────────
    const mobToggle = document.getElementById('mob-map-toggle');
    if (mobToggle) {
      const listPanel = document.querySelector('.locations-list-panel');
      const searchWrap = document.querySelector('.locations-search-wrap');
      let mapInline = false;

      mobToggle.addEventListener('click', function () {
        mapInline = !mapInline;

        if (mapInline) {
          // Move map panel inside list panel, right after the search bar
          listPanel.insertBefore(mapEl, searchWrap.nextSibling);
          mapEl.classList.add('mob-map-inline');
          mobToggle.setAttribute('aria-expanded', 'true');
          mobToggle.innerHTML =
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Hide Map';
        } else {
          // Return map panel to its original parent
          splitEl.insertBefore(mapEl, splitEl.firstChild);
          mapEl.classList.remove('mob-map-inline');
          mobToggle.setAttribute('aria-expanded', 'false');
          mobToggle.innerHTML =
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg> Show Map';
        }

        // Give Leaflet a tick to see its new dimensions
        setTimeout(function () { map.invalidateSize(); }, 60);
      });
    }
  }
})();
